import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, TimePicker } from '@douyinfe/semi-ui';
import ImageSelector from '../ImageSelector';
import RegionSelector from '../RegionSelector';

/**
 * 通用字段值输入组件，根据 rule 渲染不同类型和校验
 * @param {Object} props
 *   rule: 字段规则对象
 *   field: Form 字段名
 *   value: 当前值
 *   onChange: 值变化回调
 *   rest: 其他透传属性
 */
const FieldValueInput = ({ rule = {}, field, value, onChange, ...rest }) => {
    const { t, r, l, n, p } = rule;
    // 处理校验规则
    const rules = [];
    if (r) rules.push({ required: true, message: `请输入${n || '字段值'}` });
    if (p) {
        // 简单支持手机号等常见校验
        if (p === 'phone') {
            rules.push({ pattern: /^1\d{10}$/, message: '请输入正确的手机号' });
        } else if (typeof p === 'string') {
            try {
                // 支持正则字符串
                const reg = new RegExp(p);
                rules.push({ pattern: reg, message: `格式不正确` });
            } catch { }
        }
    }

    // 选项处理
    let options = [];
    if (l) {
        if (Array.isArray(l)) {
            options = l;
        } else if (typeof l === 'string') {
            try {
                options = l.split(",")
            } catch {
                options = [];
            }
        }
    }

    // addr2渲染逻辑
    function renderAddr2() {
        let region = [];
        let detail = '';
        if (typeof value === 'string' && value) {
            const parts = value.split(',');
            region = parts.slice(0, 3); // 省市区
            detail = parts.slice(3).join(','); // 详细地址
        }
        const [regionValue, setRegionValue] = useState(region);
        const [detailValue, setDetailValue] = useState(detail);
        // value变化时同步
        useEffect(() => {
            if (typeof value === 'string' && value) {
                const parts = value.split(',');
                setRegionValue(parts.slice(0, 3));
                setDetailValue(parts.slice(3).join(','));
            } else {
                setRegionValue([]);
                setDetailValue('');
            }
        }, [value]);
        // 统一onChange
        const handleChange = (newRegion, newDetail) => {
            const regionArr = Array.isArray(newRegion) ? newRegion : regionValue;
            const detailStr = typeof newDetail === 'string' ? newDetail : detailValue;
            const result = [...regionArr, detailStr].filter(Boolean).join(',');
            onChange && onChange(result);
        };
        return (
            <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontSize: 14, color: 'var(--semi-color-text-1)' }}>
                    {rules.some(rule => rule.required) && <span style={{ color: 'var(--semi-color-danger)' }}>*</span>}
                </div>
                <RegionSelector
                    field={field}
                    label={n}
                    rules={rules}
                    value={regionValue}
                    onChange={val => {
                        setRegionValue(val);
                        handleChange(val, detailValue);
                    }}
                    {...rest}
                />
                <Input
                    style={{ marginTop: 8 }}
                    placeholder="请输入详细地址"
                    value={detailValue}
                    onChange={val => {
                        setDetailValue(val);
                        handleChange(regionValue, val);
                    }}
                    {...rest}
                />
            </div>
        );
    }

    //console.log('fi', rule, options, t)
    // 渲染不同类型
    switch (t) {
        case 'string':
        case 'string2':
            return <Form.Input field={field} label={n} rules={rules} onChange={onChange} {...rest} />;
        case 'number':
            return <Form.Input field={field} label={n} rules={rules} onChange={onChange} type="number" {...rest} />;
        case 'select':
        case 'select2':
        case 'select3':
            return (
                <Form.Select field={field} label={n} rules={rules} onChange={onChange} {...rest}>
                    {options.map(opt => (
                        <Select.Option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</Select.Option>
                    ))}
                </Form.Select>
            );
        case 'mselect':
            // 内部自管理 value，兼容父组件不更新的情况
            const initialMselectValue = typeof value === 'string' && value ? value.split(',') : [];
            const [innerValue, setInnerValue] = useState(initialMselectValue);
            // value 变化时同步
            useEffect(() => {
                setInnerValue(typeof value === 'string' && value ? value.split(',') : []);
            }, [value]);
            return (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8, fontSize: 14, color: 'var(--semi-color-text-1)' }}>
                        {n}
                        {rules.some(rule => rule.required) && <span style={{ color: 'var(--semi-color-danger)' }}>*</span>}
                    </div>
                    <Select
                        multiple
                        style={{ width: '100%' }}
                        placeholder={`请选择${n || '选项'}`}
                        showClear
                        value={innerValue}
                        onChange={val => {
                            setInnerValue(Array.isArray(val) ? val : []);
                            const strVal = Array.isArray(val) ? val.join(',') : '';
                            onChange && onChange(strVal);
                        }}
                        {...rest}
                    >
                        {options.map(opt => {
                            const optionValue = String(opt.value || opt);
                            return (
                                <Select.Option key={optionValue} value={optionValue}>
                                    {opt.label || opt}
                                </Select.Option>
                            );
                        })}
                    </Select>
                    {rules.length > 0 && (
                        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--semi-color-text-3)' }}>
                            {rules.map((rule, index) => (
                                <div key={index}>
                                    {rule.required && '必填项'}
                                    {rule.pattern && '格式校验'}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        case 'date':
            return <Form.DatePicker field={field} label={n} rules={rules} onChange={onChange} {...rest} />;
        case 'time':
            return <Form.TimePicker field={field} label={n} rules={rules} onChange={onChange} {...rest} />;
        case 'datetime':
        case 'datetime2':
            return <Form.DatePicker field={field} type="dateTime" label={n} rules={rules} onChange={onChange} {...rest} />;
        case 'img':
        case 'imgs':
        case 'sign':
            return (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8, fontSize: 14, color: 'var(--semi-color-text-1)' }}>
                        {n}
                        {rules.some(rule => rule.required) && <span style={{ color: 'var(--semi-color-danger)' }}>*</span>}
                    </div>
                    <ImageSelector
                        value={value}
                        onChange={onChange}
                        {...rest}
                    />
                    {rules.length > 0 && (
                        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--semi-color-text-3)' }}>
                            {rules.map((rule, index) => (
                                <div key={index}>
                                    {rule.required && '必填项'}
                                    {rule.pattern && '格式校验'}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        case 'phone':
            return <Form.Input field={field} label={n} rules={[...rules, { pattern: /^1\d{10}$/, message: '请输入正确的手机号' }]} onChange={onChange} {...rest} />;
        case 'city':
            return <RegionSelector field={field} label={n} rules={rules} onChange={onChange} {...rest} />;
        case 'addr':
            return renderAddr2()
        case 'range': {
            // 用useState管理min/max
            const [range, setRange] = useState(() => {
                if (typeof value === 'string' && value.includes(',')) {
                    const [min, max] = value.split(',');
                    return { min, max };
                }
                return { min: '', max: '' };
            });
            // value变化时同步
            useEffect(() => {
                if (typeof value === 'string' && value.includes(',')) {
                    const [min, max] = value.split(',');
                    setRange({ min, max });
                } else {
                    setRange({ min: '', max: '' });
                }
            }, [value]);
            const handleRangeChange = (type, val) => {
                const newRange = { ...range, [type]: val };
                setRange(newRange);
                const result = `${newRange.min},${newRange.max}`;
                onChange && onChange(result);
            };
            return (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600 }}>
                        {n}
                        {rules.some(rule => rule.required) && <span style={{ color: 'var(--semi-color-danger)' }}>*</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Input
                            style={{ width: 100 }}
                            placeholder="最小值"
                            type="number"
                            value={range.min}
                            onChange={val => handleRangeChange('min', val)}
                            {...rest}
                        />
                        <span style={{ margin: '0 4px' }}>~</span>
                        <Input
                            style={{ width: 100 }}
                            placeholder="最大值"
                            type="number"
                            value={range.max}
                            onChange={val => handleRangeChange('max', val)}
                            {...rest}
                        />
                    </div>
                </div>
            );
        }
        default:
            return <Form.Input field={field} label={n} rules={rules} onChange={onChange} {...rest} />;
    }
};

export default FieldValueInput; 